<template>
  <v-row>
    <v-expansion-panels v-if="timetable" flat class="border-radius--none">
      <v-expansion-panel>
        <v-expansion-panel-header color="#F7F9FC" :disabled="loading">
          <v-progress-circular v-if="loading" :size="20" color="primary" indeterminate />
          <span class="H8" v-else="loading"><v-icon size="18" class="H8 custom-icon" left>mdi-clock-outline</v-icon> {{ schedule }}</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content color="#F7F9FC" class="panel-content-box">
          <div v-for="(item, index) in table" :key="index" class="d-flex align-start justify-space-between mb-2">
            <span class="schedule-text days-text" :class="{ 'days-text--active': item.current }">{{ `${ getDayOfWeek(item.day) } ${ daysOfMonthInThisWeek[item.day] }` }}</span>
            <div class="d-flex flex-wrap justify-end" style="gap: 5px">
              <v-chip
                v-if="item.hours.length === 0"
                label
                class="hours-chip"
              >
                <span class="schedule-text hours-text">{{ $t('pages.opening_hours.closed') }}</span>
              </v-chip>
              <v-chip
                v-else
                v-for="(hour, i) in item.hours"
                :key="i"
                color="#D8D9E1"
                text-color="rgba(31, 187, 237, 1)"
                label
                class="hours-chip"
              >
                <span
                  class="schedule-text hours-text"
                >
                  {{ formatRange(hour) }}
                </span>
              </v-chip>
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-row>
</template>
<script>
import OpeningHoursMixins from '@/mixins/OpeningHours'
import { formatHour, formatRange, getDayOfWeekTransalation, getDaysOfMonthOnThisWeek, getDatesOfThisWeek } from '@/lib/helpers/HoursFormatter.js'
import { getDayOfWeek } from '@/lib/helpers/Time'
import { mapState } from 'vuex'

import moment from 'moment'

export default {
  mixins: [
    OpeningHoursMixins,
  ],
  data () {
    return {
      timetable: null,
      current: null,
      datesOfThisWeek: {},
      daysOfMonthInThisWeek: {},
      loading: false,
    }
  },
  props: {
    pageId: {
      default: null,
    },
  },
  created () {
    moment.locale(this.$i18n.locale)
    this.loading = true
    this.$store.dispatch('pages/getPageOpeningHours', {
      pageId: this.pageId,
      start: moment().startOf('week').format('YYYY-MM-DD'),
      end: moment().endOf('week').format('YYYY-MM-DD'),
    }).then((openingHours) => {
      if (openingHours.length > 0) {
        this.timetable = this.getWeekTimeTable(openingHours)
        this.$emit('updateTimetable', this.timetable)
        this.getCurrent()
      }
    }).finally(() => {
      this.loading = false
    })
    this.datesOfThisWeek = getDatesOfThisWeek(this.settings.webappUser.firstDayOfWeek)
    this.daysOfMonthInThisWeek = getDaysOfMonthOnThisWeek(this.settings.webappUser.firstDayOfWeek)
  },
  computed: {
    ...mapState({
      settings: state => state.me.settings,
    }),
    weekdays () {
      const wd = []
      for (let index = getDayOfWeek(this.settings.webappUser.firstDayOfWeek); wd.length < 7; index++) {
        if (index === 7) {
          index = 0
        }
        wd.push(index)
      }
      return wd
    },
    table () {
      return this.weekdays.map((day) => {
        const item = this.timetable ? this.timetable.find((item) => { return item.day === day }) : null
        if (item) {
          return {
            day: day,
            current: item.current,
            hours: item.hours,
          }
        } else {
          return {
            day: day,
            current: null,
            hours: [],
          }
        }
      })
    },
    nextOpenDay () {
      const indexOfToday = this.table.findIndex(({current}) => current)
      if (indexOfToday < 0) return null
      for (let i = indexOfToday; i < this.table.length; i++) {
        const { hours } = this.table[i]
        if (hours.length > 0) return this.table[i]
      }
      for (let i = 0; i < indexOfToday; i++) {
        const { hours } = this.table[i]
        if (hours.length > 0) return this.table[i]
      }
      return null
    },
    nextOpenHours () {
      if (!this.nextOpenDay) return null
      const { hours, day, current } = this.nextOpenDay
      const isOpenNow = current && this.checkHoursInRange(hours)
      return {
        startHour: isOpenNow ?
          this.$t('pages.opening_hours.schedule.now') :
          this.$t('pages.opening_hours.schedule.open', { day: this.getDayOfWeek(day), startHour: formatHour(hours[0].startHour) }),
        endHour: this.$t('pages.opening_hours.schedule.close', { endHour: formatHour(hours[0].endHour) }),
      }
    },
    schedule () {
      if (!this.nextOpenDay) return this.$t('pages.opening_hours.closed')
      const { startHour, endHour } = this.nextOpenHours
      return  `${ startHour } - ${ endHour }`
    },
  },
  methods: {
    formatRange (hour) {
      return formatRange(hour)
    },
    getDayOfWeek (day) {
      return getDayOfWeekTransalation(day)
    },
    getDateWithDayOfWeek (day) {
      return getDateWithDayOfWeek(day)
    },
    checkHourInRange (hour) {
      if (!hour) return false
      const now = moment()
      const start = moment().startOf('day').add(hour.startHour, 'ms')
      const end = hour.endHour < hour.startHour ? moment().endOf('day').add(hour.endHour, 'ms'): moment().startOf('day').add(hour.endHour, 'ms')
      return now.isAfter(start) && now.isBefore(end)
    },
    checkHoursInRange (hours) {
      const isInRangeHours = hours.some((hour) => {
        return this.checkHourInRange(hour)
      })
      return hours.length > 0 && isInRangeHours
    },
  },
}
</script>
<style scoped>
.border-radius--none {
  border-radius: 0px;
}

.panel-content-box {
  --shadow-color: 0deg 0% 80%;
  box-shadow: 0px 10px 20px 0px rgba(0, 0, 0, 0.10);
  border-radius: 0px 0px 3px 3px;
}

.hours-chip {
  height: 24px;
  padding: 10px 6px;
}

.schedule-text {
  color: #1A1D44;
  font-family: Mukta Mahee;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}

.days-text {
  font-size: 12px;
  padding-left: 8px;
  padding-right: 5px;
  flex: 1 0 auto;
}

.hours-text {
  font-size: 10px;
  padding: 0 6px;
}

.days-text--active::before {
  content: "";
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  background: var(--v-primary-lighten1);
  margin-right: 0.5rem;
  margin-left: -1rem;
  display: inline-block;
}

.custom-icon::before {
  font-weight: bold;
  color: #1A1D44;
  margin-bottom: 2px;
}

.v-icon--left {
  margin-right: 5px;
}
</style>